import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X, RotateCcw, ClipboardList, Users, Newspaper, ExternalLink, Link2 } from "lucide-react";
import LinkResolver from "../components/tasks/LinkResolver";
import { api } from "../lib/api";

const TYPE = {
    lines: { l: "Lines", Icon: ClipboardList, c: "#9D4CDD" },
    roster: { l: "Roster", Icon: Users, c: "#F5B301" },
    news: { l: "Validate", Icon: Newspaper, c: "#2DD4BF" },
    link: { l: "Link Player", Icon: Link2, c: "#EF4444" },
};
const fmtDay = (d) => { try { return new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); } catch { return d; } };

function TaskRow({ t, onAct, done }) {
    const ty = TYPE[t.type] || TYPE.lines;
    return (
        <li className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${done ? "border-white/[0.06] bg-white/[0.015] opacity-60" : "border-white/10 bg-white/[0.03]"}`} data-testid={`task-${t.key}`}>
            <span className="mt-0.5 h-8 w-8 shrink-0 rounded-lg flex items-center justify-center" style={{ background: `${ty.c}22`, color: ty.c }}><ty.Icon className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: `${ty.c}22`, color: ty.c }}>{t.league?.toUpperCase()} · {ty.l}</span>
                    <span className={`text-sm font-bold ${done ? "line-through text-white/50" : "text-white"}`}>{t.title}</span>
                    {done && <span className="text-[10px] text-white/50" data-testid={`task-by-${t.key}`}>{t.status === "dismissed" ? "Dismissed" : t.type === "link" ? "Linked" : t.auto ? "Lineups saved" : "Done"}{t.done_by ? ` by ${t.done_by}` : ""}{t.done_at ? ` · ${new Date(t.done_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}</span>}
                </div>
                {t.detail && <p className="text-xs text-white/50 mt-0.5">{t.detail}</p>}
                {!!t.links?.length && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {t.links.map((l, i) => (l.href
                            ? <a key={i} href={l.href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] font-bold text-[#C9A6F0] hover:text-white px-2 py-0.5 rounded-md bg-white/5 border border-white/10">{l.label}<ExternalLink className="h-3 w-3" /></a>
                            : <Link key={i} to={l.to} className="text-[11px] font-bold text-[#C9A6F0] hover:text-white px-2 py-0.5 rounded-md bg-white/5 border border-white/10">{l.label}</Link>))}
                    </div>
                )}
                {!done && t.type === "link" && <LinkResolver t={t} />}
            </div>
            <div className="flex gap-1.5 shrink-0">
                {done ? (
                    <button type="button" onClick={() => onAct(t.key, "reopen")} title="Reopen" data-testid={`task-reopen-${t.key}`} className="h-8 w-8 rounded-lg border border-white/10 text-white/60 hover:text-white flex items-center justify-center"><RotateCcw className="h-4 w-4" /></button>
                ) : (<>
                    {["lines", "link"].includes(t.type) ? <span className="h-8 px-2 rounded-lg border border-white/10 text-[9px] font-black uppercase tracking-wider text-white/40 flex items-center" title={t.type === "link" ? "Completes once a player is linked" : "Completes automatically once both lineups are saved"} data-testid={`task-auto-${t.key}`}>Auto</span> : <button type="button" onClick={() => onAct(t.key, "done")} title="Done" data-testid={`task-done-${t.key}`} className="h-8 w-8 rounded-lg bg-[#22C55E] text-black flex items-center justify-center hover:bg-[#16A34A]"><Check className="h-4 w-4" strokeWidth={3} /></button>}
                    <button type="button" onClick={() => onAct(t.key, "dismiss")} title="Dismiss" data-testid={`task-dismiss-${t.key}`} className="h-8 w-8 rounded-lg border border-white/10 text-white/60 hover:text-white flex items-center justify-center"><X className="h-4 w-4" /></button>
                </>)}
            </div>
        </li>
    );
}

export default function Tasks() {
    const qc = useQueryClient();
    const [tab, setTab] = useState("open");
    const { data, isLoading } = useQuery({ queryKey: ["admin-tasks"], queryFn: () => api.get("/admin/tasks").then((r) => r.data), refetchInterval: 60 * 1000 });
    const act = useMutation({
        mutationFn: ({ key, action }) => api.post(`/admin/tasks/${encodeURIComponent(key)}/${action}`),
        onSuccess: () => { ["admin-tasks", "admin-tasks-count", "admin-tasks-links"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); },
        onError: () => toast.error("Could not update task"),
    });
    const onAct = (key, action) => act.mutate({ key, action });
    const list = (tab === "open" ? data?.open : data?.done) || [];
    const byDay = list.reduce((m, t) => { (m[t.date || ""] = m[t.date || ""] || []).push(t); return m; }, {});

    return (
        <div className="mx-auto max-w-4xl px-4 py-6 fade-up" data-testid="tasks-page">
            <div className="flex items-center justify-between gap-3 mb-5">
                <h1 className="text-2xl font-black uppercase tracking-tight text-white">Tasks</h1>
                <div className="flex gap-1.5">
                    {[["open", `Open${data ? ` (${data.count})` : ""}`], ["done", "Done"]].map(([k, l]) => (
                        <button key={k} type="button" onClick={() => setTab(k)} data-testid={`tasks-tab-${k}`}
                            className={`px-3.5 py-1.5 rounded-lg border text-xs font-black uppercase tracking-wider ${tab === k ? "bg-[#9D4CDD] border-[#9D4CDD] text-white" : "border-white/10 text-white/60 hover:text-white"}`}>{l}</button>
                    ))}
                </div>
            </div>
            {isLoading ? <p className="text-white/50 text-sm">Loading…</p> : !list.length ? (
                <p className="text-white/50 text-sm py-10 text-center" data-testid="tasks-empty">{tab === "open" ? "All caught up — nothing to action." : "No completed tasks yet."}</p>
            ) : (
                <div className="space-y-6">
                    {Object.entries(byDay).sort(([a], [b]) => (tab === "open" ? a.localeCompare(b) : b.localeCompare(a))).map(([day, ts]) => (
                        <section key={day || "none"}>
                            <h2 className="text-[11px] font-black uppercase tracking-wider text-white/45 mb-2">{day ? fmtDay(day) : "Undated"} · {ts.length}</h2>
                            <ul className="space-y-2">{ts.map((t) => <TaskRow key={t.key} t={t} onAct={onAct} done={tab === "done"} />)}</ul>
                        </section>
                    ))}
                </div>
            )}
        </div>
    );
}
